import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Search, Box, Trash2, Edit2, PackageSearch, X, Beaker, Save, ChevronDown, ChevronUp, Download, Upload, Sparkles, RefreshCw, FileText, ExternalLink } from 'lucide-react';
import Papa from 'papaparse';
import './ItemManagement.css';

const CHEMICAL_COMPOUNDS = [
    'Hydrogen (H)', 'Helium (He)', 'Lithium (Li)', 'Beryllium (Be)', 'Boron (B)',
    'Carbon (C)', 'Nitrogen (N)', 'Oxygen (O)', 'Fluorine (F)', 'Neon (Ne)',
    'Sodium (Na)', 'Magnesium (Mg)', 'Aluminum (Al)', 'Silicon (Si)', 'Phosphorus (P)',
    'Sulfur (S)', 'Chlorine (Cl)', 'Argon (Ar)', 'Potassium (K)', 'Calcium (Ca)',
    'Scandium (Sc)', 'Titanium (Ti)', 'Vanadium (V)', 'Chromium (Cr)', 'Manganese (Mn)',
    'Iron (Fe)', 'Cobalt (Co)', 'Nickel (Ni)', 'Copper (Cu)', 'Zinc (Zn)',
    'Gallium (Ga)', 'Germanium (Ge)', 'Arsenic (As)', 'Selenium (Se)', 'Bromine (Br)',
    'Krypton (Kr)', 'Rubidium (Rb)', 'Strontium (Sr)', 'Yttrium (Y)', 'Zirconium (Zr)',
    'Niobium (Nb)', 'Molybdenum (Mo)', 'Technetium (Tc)', 'Ruthenium (Ru)', 'Rhodium (Rh)',
    'Palladium (Pd)', 'Silver (Ag)', 'Cadmium (Cd)', 'Indium (In)', 'Tin (Sn)',
    'Antimony (Sb)', 'Tellurium (Te)', 'Iodine (I)', 'Xenon (Xe)', 'Cesium (Cs)',
    'Barium (Ba)', 'Lanthanum (La)', 'Cerium (Ce)', 'Praseodymium (Pr)', 'Neodymium (Nd)',
    'Promethium (Pm)', 'Samarium (Sm)', 'Europium (Eu)', 'Gadolinium (Gd)', 'Terbium (Tb)',
    'Dysprosium (Dy)', 'Holmium (Ho)', 'Erbium (Er)', 'Thulium (Tm)', 'Ytterbium (Yb)',
    'Lutetium (Lu)', 'Hafnium (Hf)', 'Tantalum (Ta)', 'Tungsten (W)', 'Rhenium (Re)',
    'Osmium (Os)', 'Iridium (Ir)', 'Platinum (Pt)', 'Gold (Au)', 'Mercury (Hg)',
    'Thallium (Tl)', 'Lead (Pb)', 'Bismuth (Bi)', 'Polonium (Po)', 'Astatine (At)',
    'Radon (Rn)', 'Francium (Fr)', 'Radium (Ra)', 'Actinium (Ac)', 'Thorium (Th)',
    'Protactinium (Pa)', 'Uranium (U)', 'Neptunium (Np)', 'Plutonium (Pu)', 'Americium (Am)',
    'Curium (Cm)', 'Berkelium (Bk)', 'Californium (Cf)', 'Einsteinium (Es)', 'Fermium (Fm)',
    'Mendelevium (Md)', 'Nobelium (No)', 'Lawrencium (Lr)', 'Rutherfordium (Rf)', 'Dubnium (Db)',
    'Seaborgium (Sg)', 'Bohrium (Bh)', 'Hassium (Hs)', 'Meitnerium (Mt)', 'Darmstadtium (Ds)',
    'Roentgenium (Rg)', 'Copernicium (Cn)', 'Nihonium (Nh)', 'Flerovium (Fl)', 'Moscovium (Mc)',
    'Livermorium (Lv)', 'Tennessine (Ts)', 'Oganesson (Og)'
];

// removed static mocks

const SearchableSelect = ({
    value,
    options,
    onChange,
    placeholder = "Select...",
    disabledOptions = [],
    isOpen: controlledIsOpen,
    onToggle
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

    const setIsOpen = (val) => {
        const nextVal = typeof val === 'function' ? val(isOpen) : val;
        if (onToggle) onToggle(nextVal);
        else setInternalIsOpen(nextVal);
    };

    const [search, setSearch] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const filteredOptions = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

    const handleSelect = (e, opt) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const normalizedDisabled = disabledOptions ? disabledOptions.map(d => (d || '').trim().toLowerCase()) : [];
        const isCurrentValue = (value || '').trim().toLowerCase() === (opt || '').trim().toLowerCase();
        const isDisabled = normalizedDisabled.includes((opt || '').trim().toLowerCase()) && !isCurrentValue;

        if (isDisabled) return;

        onChange(opt);
        setIsOpen(false);
        setSearch('');
    };

    return (
        <div
            className={`searchable-select ${isOpen ? 'is-open' : ''}`}
            ref={dropdownRef}
        >
            <div
                className="input-field select-field flex items-center justify-between cursor-pointer bg-white"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="truncate">{value || placeholder}</span>
                <ChevronDown size={16} className={`searchable-select-chevron ${isOpen ? 'rotate' : ''}`} />
            </div>

            {isOpen && (
                <div className="dropdown-menu-card">
                    <div className="dropdown-search-header">
                        <div className="dropdown-search-pill">
                            <Search size={14} className="dropdown-search-icon" />
                            <input
                                type="text"
                                autoFocus
                                className="dropdown-search-input"
                                placeholder="Search element..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const normalizedDisabled = disabledOptions ? disabledOptions.map(d => (d || '').trim().toLowerCase()) : [];
                                        const firstSelectable = filteredOptions.find(opt => {
                                            const isCurrent = (value || '').trim().toLowerCase() === (opt || '').trim().toLowerCase();
                                            return !normalizedDisabled.includes((opt || '').trim().toLowerCase()) || isCurrent;
                                        });
                                        if (firstSelectable) {
                                            handleSelect(e, firstSelectable);
                                        }
                                    } else if (e.key === 'Escape') {
                                        setIsOpen(false);
                                    }
                                }}
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dropdown-search-clear"
                                    onClick={() => setSearch('')}
                                    title="Clear search"
                                >
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="dropdown-options-list">
                        {filteredOptions.length > 0 ? filteredOptions.map(opt => {
                            const normalizedDisabled = disabledOptions ? disabledOptions.map(d => (d || '').trim().toLowerCase()) : [];
                            const isCurrentValue = (value || '').trim().toLowerCase() === (opt || '').trim().toLowerCase();
                            const isDisabled = normalizedDisabled.includes((opt || '').trim().toLowerCase()) && !isCurrentValue;
                            return (
                                <div
                                    key={opt}
                                    className={`dropdown-option-item ${isDisabled ? 'disabled' : ''} ${isCurrentValue ? 'selected' : ''}`}
                                    onMouseDown={(e) => handleSelect(e, opt)}
                                    onClick={(e) => handleSelect(e, opt)}
                                    title={isDisabled ? "Already selected in another row" : ""}
                                >
                                    <span>{opt}</span>
                                    {isDisabled && (
                                        <span className="dropdown-option-badge">
                                            Added
                                        </span>
                                    )}
                                </div>
                            );
                        }) : (
                            <div className="dropdown-empty-state">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const ItemManagement = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const userModule = localStorage.getItem('userModule') || 'CU';
        const userId = localStorage.getItem('userId') || localStorage.getItem('currentUser') || 'admin';
        fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/items?module=${userModule}&userId=${encodeURIComponent(userId)}`, {
            headers: { 'x-module': userModule, 'x-user-id': userId }
        })
            .then(res => res.json())
            .then(data => {
                // map _id to id universally for UI bindings
                setItems(data.map(i => ({ ...i, id: i._id })));
            })
            .catch(err => console.error("Error fetching items: ", err));
    }, []);

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isFetchingAI, setIsFetchingAI] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'info' });

    const showToast = (message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
    };

    // Expanded Row State
    const [expandedItemId, setExpandedItemId] = useState(null);
    const [openCompoundIndex, setOpenCompoundIndex] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        itemCode: '',
        alloyCode: '',
        customerName: '',
        compounds: []
    });

    // Spec Upload & Preview Modal State
    const [specPreviewModal, setSpecPreviewModal] = useState(null);

    const isCopperElement = (elem) => {
        const str = (elem || '').trim().toUpperCase();
        return str.includes('COPPER') || str.includes('(CU)') || str === 'CU';
    };

    const matchChemicalCompound = (rawElement) => {
        if (!rawElement) return CHEMICAL_COMPOUNDS[0];
        const cleanRaw = String(rawElement).trim();
        const upperRaw = cleanRaw.toUpperCase();

        // 1. Direct match with CHEMICAL_COMPOUNDS
        const directMatch = CHEMICAL_COMPOUNDS.find(cc => cc.toUpperCase() === upperRaw);
        if (directMatch) return directMatch;

        // 2. Match symbol in parentheses, e.g. "Copper (Cu)" -> "CU"
        const bySymbol = CHEMICAL_COMPOUNDS.find(cc => {
            const parenMatch = cc.match(/\(([A-Za-z0-9]+)\)/);
            return parenMatch && parenMatch[1].toUpperCase() === upperRaw;
        });
        if (bySymbol) return bySymbol;

        // 3. Match element name before parentheses, e.g. "Copper" from "Copper (Cu)"
        const byName = CHEMICAL_COMPOUNDS.find(cc => {
            const namePart = cc.replace(/\s*\([^)]*\)/, '').trim().toUpperCase();
            return namePart === upperRaw;
        });
        if (byName) return byName;

        // 4. Starts with or partial match
        const partialMatch = CHEMICAL_COMPOUNDS.find(cc => 
            cc.toUpperCase().startsWith(upperRaw) || upperRaw.startsWith(cc.replace(/\s*\([^)]*\)/, '').trim().toUpperCase())
        );
        if (partialMatch) return partialMatch;

        return cleanRaw;
    };

    const handleSpecFileSelect = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const isImage = file.type.startsWith('image/');

        if (!isPdf && !isImage) {
            showToast("Please upload a valid image (PNG, JPG, WebP) or PDF file.", "error");
            e.target.value = '';
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        setSpecPreviewModal({
            file,
            previewUrl,
            isPdf,
            fileName: file.name,
            fileSize: (file.size / 1024).toFixed(1) + ' KB',
            zoom: 1,
            isExtracting: false
        });
        e.target.value = '';
    };

    const handleCloseSpecPreview = () => {
        if (specPreviewModal?.previewUrl) {
            URL.revokeObjectURL(specPreviewModal.previewUrl);
        }
        setSpecPreviewModal(null);
    };

    const handleProceedSpecExtract = async () => {
        if (!specPreviewModal?.file) return;

        setSpecPreviewModal(prev => ({ ...prev, isExtracting: true }));

        try {
            const formDataPayload = new FormData();
            formDataPayload.append('image', specPreviewModal.file);

            const baseUrl = (process.env.REACT_APP_ABSTRACT_SERVER_BASE_URL || '').trim().replace(/\/$/, '');
            const res = await fetch(`${baseUrl}/api/items/extract-spec`, {
                method: 'POST',
                body: formDataPayload
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || `Failed to extract specification (${res.status})`);
            }

            // Parse response output
            let result = data;
            if (typeof result === 'string') {
                try {
                    result = JSON.parse(result.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim());
                } catch (err) {}
            }
            if (result && result.output) {
                if (typeof result.output === 'string') {
                    try {
                        result = JSON.parse(result.output.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim());
                    } catch (err) {
                        result = result.output;
                    }
                } else {
                    result = result.output;
                }
            }

            // Extract records / header / elements
            let header = {};
            let elements = [];
            let needsChecking = [];

            if (result?.records && Array.isArray(result.records) && result.records.length > 0) {
                header = result.records[0].header || {};
                elements = result.records[0].elements || [];
                needsChecking = result.records[0].needs_checking || [];
            } else if (result?.header || result?.elements) {
                header = result.header || {};
                elements = result.elements || [];
                needsChecking = result.needs_checking || [];
            } else if (Array.isArray(result)) {
                elements = result;
            }

            const mappedCompounds = (Array.isArray(elements) ? elements : [])
                .filter(el => el && (el.element || el.element_name || el.symbol))
                .map(el => {
                    const rawElem = el.element || el.element_name || el.symbol;
                    const matchedElem = matchChemicalCompound(rawElem);
                    return {
                        element: matchedElem,
                        spec_min: el.spec_min !== undefined && el.spec_min !== null ? String(el.spec_min) : '',
                        spec_max: el.spec_max !== undefined && el.spec_max !== null ? String(el.spec_max) : '',
                        target_min: el.target_min !== undefined && el.target_min !== null ? String(el.target_min) : '',
                        target_max: el.target_max !== undefined && el.target_max !== null ? String(el.target_max) : ''
                    };
                });

            const uniqueCompounds = [];
            const seenElements = new Set();
            mappedCompounds.forEach(c => {
                if (!seenElements.has(c.element)) {
                    seenElements.add(c.element);
                    uniqueCompounds.push(c);
                }
            });

            // Only populate specification composition matrix, preserving manually entered Item Name, code, alloy code, and customer name
            setFormData(prev => ({
                ...prev,
                compounds: uniqueCompounds.length > 0 ? uniqueCompounds : prev.compounds
            }));

            handleCloseSpecPreview();
            setIsModalOpen(true);

            if (needsChecking && needsChecking.length > 0) {
                const flagged = needsChecking.map(n => `${n.element || ''} (${n.reason || 'check'})`).filter(Boolean).join(', ');
                showToast(`Specification extracted! Please review flagged items: ${flagged}`, "warning");
            } else {
                showToast("Specification composition matrix populated successfully!", "success");
            }
        } catch (error) {
            console.error("Error extracting specification:", error);
            showToast(error.message || "Failed to extract specification from file.", "error");
            setSpecPreviewModal(prev => prev ? ({ ...prev, isExtracting: false }) : null);
        }
    };

    // Global Element Settings States
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [globalSettings, setGlobalSettings] = useState([]);
    const [isLoadingSettings, setIsLoadingSettings] = useState(false);
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [settingsSearchTerm, setSettingsSearchTerm] = useState('');

    useEffect(() => {
        const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
        if (userRole === 'supervisor' || userRole === 'shop floor operator' || userRole === 'operator') {
            alert("Access Restricted: Your role does not have permission to access Master Specifications.");
            navigate('/dashboard', { replace: true });
            return;
        }
        fetchGlobalSettings();
    }, []);

    const fetchGlobalSettings = async () => {
        try {
            const userModule = localStorage.getItem('userModule') || 'CU';
            const userId = localStorage.getItem('userId') || localStorage.getItem('currentUser') || 'admin';
            const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/element-settings?module=${userModule}&userId=${encodeURIComponent(userId)}`, {
                headers: { 'x-module': userModule, 'x-user-id': userId }
            });
            if (res.ok) {
                const data = await res.json();
                setGlobalSettings(data);
            }
        } catch (error) {
            console.error("Error fetching global settings", error);
        }
    };

    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.itemCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.alloyCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.customerName && item.customerName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const toggleExpand = (id) => {
        setExpandedItemId(expandedItemId === id ? null : id);
    };

    const handleOpenModal = (item = null) => {
        setOpenCompoundIndex(null);
        if (item) {
            setEditingItem(item);
            setFormData({ ...item });
        } else {
            setEditingItem(null);
            setFormData({
                name: '',
                itemCode: '',
                alloyCode: '',
                customerName: '',
                compounds: [{ element: CHEMICAL_COMPOUNDS[0], spec_min: '', spec_max: '', target_min: '', target_max: '' }]
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
        setOpenCompoundIndex(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddCompound = () => {
        const usedElements = new Set(formData.compounds.map(c => c.element));
        const nextAvailable = CHEMICAL_COMPOUNDS.find(c => !usedElements.has(c));

        if (!nextAvailable) {
            showToast("All available elements have already been added.", "warning");
            return;
        }

        setFormData(prev => ({
            ...prev,
            compounds: [...prev.compounds, { element: nextAvailable, spec_min: '', spec_max: '', target_min: '', target_max: '' }]
        }));
    };

    const handleRemoveCompound = (index) => {
        setFormData(prev => {
            const newCompounds = [...prev.compounds];
            newCompounds.splice(index, 1);
            return { ...prev, compounds: newCompounds };
        });
    };

    const handleCompoundChange = (index, field, value) => {
        setFormData(prev => {
            const newCompounds = [...prev.compounds];
            newCompounds[index] = { ...newCompounds[index], [field]: value };
            return { ...prev, compounds: newCompounds };
        });
    };

    const handleFetchDetails = async () => {
        if (!formData.itemCode) return;
        setIsFetchingAI(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/items/fetch-details/${encodeURIComponent(formData.itemCode.trim())}`);
            if (res.ok) {
                const data = await res.json();
                console.log("Fetched details:", data);
                if (data && data.composition && Array.isArray(data.composition)) {
                    // Filter empty composition rows and map
                    const fetchedCompounds = data.composition
                        .filter(c => c.element && c.element.trim() !== '')
                        .map(c => {
                            const apiSymbol = c.element.trim().toUpperCase();
                            // Match by extracting the symbol from "Element Name (SYM)" parenthetical
                            // This prevents short symbols like "C" from incorrectly matching "Cu", "Co", etc.
                            const matchedElement = CHEMICAL_COMPOUNDS.find(cc => {
                                const parenMatch = cc.match(/\(([A-Za-z0-9]+)\)/);
                                const ccSymbol = parenMatch ? parenMatch[1].toUpperCase() : cc.split(' ')[0].toUpperCase();
                                return ccSymbol === apiSymbol;
                            });

                            return {
                                element: matchedElement || c.element,
                                spec_min: c.min ?? '',
                                spec_max: c.max ?? '',
                                target_min: '',
                                target_max: ''
                            };
                        });

                    if (fetchedCompounds.length > 0) {
                        setFormData(prev => ({ ...prev, compounds: fetchedCompounds }));
                    } else {
                        showToast("Composition was not found. You can still enter your own set of compositions manually.", "warning");
                    }
                } else {
                    showToast("Composition was not found. You can still enter your own set of compositions manually.", "warning");
                }
            } else {
                console.error("Failed to fetch details:", res.status);
            }
        } catch (error) {
            console.error("Failed to fetch details", error);
        } finally {
            setIsFetchingAI(false);
        }
    };

    // const handleDownloadTemplate = () => {
    //     const templateContent = 'Element,Min,Max\nCarbon (C),0.00,0.50\nManganese (Mn),0.00,0.90\nSilicon (Si),0.00,0.00\n';
    //     const blob = new Blob([templateContent], { type: 'text/csv;charset=utf-8;' });
    //     const link = document.createElement('a');
    //     const url = URL.createObjectURL(blob);
    //     link.setAttribute('href', url);
    //     link.setAttribute('download', 'compound_template.csv');
    //     link.style.visibility = 'hidden';
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };
    const handleDownloadTemplate = () => {
        const headers = 'Element,Spec Min,Spec Max,Target Min,Target Max\n';

        const rows = CHEMICAL_COMPOUNDS
            .map(element => `${element},,,,`)
            .join('\n');

        const templateContent = headers + rows;

        const blob = new Blob([templateContent], {
            type: 'text/csv;charset=utf-8;'
        });

        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', 'compound_template.csv');
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    };
    const handleUploadCSV = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (results.data && results.data.length > 0) {
                    const newCompounds = [];
                    results.data.forEach(row => {
                        const getValue = (...keys) => {
                            for (const key of keys) {
                                if (row[key] !== undefined && row[key] !== null) {
                                    return String(row[key]).trim();
                                }
                            }
                            return '';
                        };

                        const element = getValue('Element', 'element', 'Compound Element');
                        const spec_min = getValue('Spec Min', 'spec_min', 'Min', 'min');
                        const spec_max = getValue('Spec Max', 'spec_max', 'Max', 'max');
                        const target_min = getValue('Target Min', 'target_min');
                        const target_max = getValue('Target Max', 'target_max');

                        const isCopper = isCopperElement(element);
                        // Only include if element name exists and at least one spec/target value is provided (or min if Copper)
                        if (element && (spec_min !== '' || spec_max !== '' || target_min !== '' || target_max !== '' || (isCopper && (spec_min !== '' || target_min !== '')))) {
                            if (!newCompounds.some(c => c.element.toLowerCase() === element.toLowerCase())) {
                                newCompounds.push({
                                    element,
                                    spec_min,
                                    spec_max,
                                    target_min,
                                    target_max
                                });
                            }
                        }
                    });

                    if (newCompounds.length > 0) {
                        setFormData(prev => ({
                            ...prev,
                            compounds: newCompounds
                        }));
                        showToast(`Successfully uploaded ${newCompounds.length} composition rules.`, "success");
                    } else {
                        showToast("No valid composition rules found in the CSV. Please fill in Spec Min/Max or Target Min/Max values.", "warning");
                    }
                }
                // Reset input
                e.target.value = '';
            },
            error: (error) => {
                console.error("Error parsing CSV:", error);
                showToast("Failed to parse CSV file.", "error");
                e.target.value = '';
            }
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        console.log("formData", formData);

        const isPresent = (val) => {
            if (val === null || val === undefined) return false;
            const strVal = String(val).trim();
            if (strVal === '' || strVal.toLowerCase() === 'null') return false;
            // const numVal = Number(strVal);
            // if (isNaN(numVal) || numVal === 0) return false;
            return true;
        };

        // Prevent duplicate elements
        const elementNames = formData.compounds.map(c => c.element && c.element.trim()).filter(Boolean);
        const uniqueElements = new Set(elementNames);
        if (uniqueElements.size !== elementNames.length) {
            showToast("Duplicate elements found. Each element in the composition matrix must be unique.", "error");
            return;
        }

        const isValid = formData.compounds.every(c => {
            console.log("Validating compound:", c);
            const hasMin = isPresent(c.target_min) || isPresent(c.spec_min);
            const hasMax = isPresent(c.target_max) || isPresent(c.spec_max);
            const isCopper = isCopperElement(c.element);
            if (isCopper) {
                return hasMin; // For copper, max value is optional
            }
            return hasMin && hasMax;
        });
        console.log("isValid", isValid);
        if (!isValid) {
            showToast("For each compound (except Copper), at least 'spec min' or 'target min' AND 'spec max' or 'target max' must be filled. Copper requires only a min value.", "error");
            return;
        }

        try {
            const userModule = localStorage.getItem('userModule') || 'CU';
            const userId = localStorage.getItem('userId') || localStorage.getItem('currentUser') || 'admin';
            const sanitizedCompounds = formData.compounds.map(c => ({
                ...c,
                spec_min: isPresent(c.spec_min) ? Number(c.spec_min) : null,
                spec_max: isPresent(c.spec_max) ? Number(c.spec_max) : null,
                target_min: isPresent(c.target_min) ? Number(c.target_min) : null,
                target_max: isPresent(c.target_max) ? Number(c.target_max) : null,
            }));
            const payload = { ...formData, compounds: sanitizedCompounds, module: userModule, userId: userId };
            const reqHeaders = {
                'Content-Type': 'application/json',
                'x-module': userModule,
                'x-user-id': userId
            };

            if (editingItem) {
                const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/items/${editingItem.id}`, {
                    method: 'PUT',
                    headers: reqHeaders,
                    body: JSON.stringify(payload)
                });
                const updated = await res.json();

                if (!res.ok) {
                    const errorMsg = updated.message && updated.message.includes('E11000')
                        ? 'Item Code or Alloy Code already exists!'
                        : (updated.message || 'Error updating specification');
                    showToast(errorMsg, "error");
                    return;
                }

                setItems(items.map(i => i.id === editingItem.id ? { ...updated, id: updated._id } : i));
                showToast('Specification successfully updated', 'success');
            } else {
                const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/items`, {
                    method: 'POST',
                    headers: reqHeaders,
                    body: JSON.stringify(payload)
                });
                const added = await res.json();

                if (!res.ok) {
                    const errorMsg = added.message && added.message.includes('E11000')
                        ? `An item with Item Code "${formData.itemCode}" already exists. Please choose a unique code.`
                        : (added.message || 'Error creating specification');
                    showToast(errorMsg, "error");
                    return;
                }

                setItems([...items, { ...added, id: added._id }]);
                showToast('New specification successfully loaded to database', 'success');
            }
        } catch (error) {
            console.error(error);
            showToast('Network collision / API error connecting to database', 'error');
            return;
        }
        handleCloseModal();
    };

    const handleDelete = async (id) => {
        try {
            await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/items/${id}`, { method: 'DELETE' });
            setItems(items.filter(i => i.id !== id));
            if (expandedItemId === id) {
                setExpandedItemId(null);
            }
        } catch (error) {
            console.error("Failed to delete", error);
        }
    };

    // const handleGlobalSettingChange = (element, value) => {
    //     setGlobalSettings(prev => {
    //         const existing = prev.find(s => s.element === element);
    //         if (existing) {
    //             return prev.map(s => s.element === element ? { ...s, loss_factor: parseFloat(value) || 0 } : s);
    //         } else {
    //             return [...prev, { element, loss_factor: parseFloat(value) || 0 }];
    //         }
    //     });
    // };

    const handleGlobalSettingChange = (element, value) => {
        setGlobalSettings(prev => {
            const existing = prev.find(s => s.element === element);

            if (existing) {
                return prev.map(s =>
                    s.element === element
                        ? { ...s, loss_factor: value }
                        : s
                );
            }

            return [...prev, { element, loss_factor: value }];
        });
    };

    const saveGlobalSettings = async () => {
        setIsSavingSettings(true);
        try {
            const userModule = localStorage.getItem('userModule') || 'CU';
            const userId = localStorage.getItem('userId') || localStorage.getItem('currentUser') || 'admin';
            const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/element-settings/bulk?module=${userModule}&userId=${encodeURIComponent(userId)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'x-module': userModule,
                    'x-user-id': userId
                },
                body: JSON.stringify({ settings: globalSettings, module: userModule, userId: userId })
            });
            if (res.ok) {
                showToast("Global elemental settings saved successfully", "success");
                setIsSettingsModalOpen(false);
                setSettingsSearchTerm('');
            } else {
                showToast("Failed to save global settings", "error");
            }
        } catch (error) {
            console.error(error);
            showToast("Error connecting to server", "error");
        } finally {
            setIsSavingSettings(false);
        }
    };

    return (
        <div className="im-container">
            <AnimatePresence>
                {toast.show && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, x: '-50%' }}
                        animate={{ opacity: 1, y: 20, x: '-50%' }}
                        exit={{ opacity: 0, y: -20, x: '-50%' }}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: '50%',
                            zIndex: 9999,
                            background: toast.type === 'error' ? '#fee2e2' : '#fef3c7',
                            border: `1px solid ${toast.type === 'error' ? '#f87171' : '#fcd34d'}`,
                            color: toast.type === 'error' ? '#991b1b' : '#92400e',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        {toast.message}
                    </motion.div>
                )}
            </AnimatePresence>

            <nav className="dashboard-nav glass-panel">
                <div className="nav-brand">
                    <PackageSearch className="text-gradient-icon" color="var(--primary-color)" size={28} />
                    <h2 className="text-gradient m-0">Item Specification Matrix</h2>
                </div>
                <div className="nav-actions">
                    <button
                        className="btn-secondary"
                        onClick={() => navigate('/dashboard')}
                    >
                        <ArrowLeft size={18} />
                        Terminal
                    </button>
                </div>
            </nav>

            <main className="im-content">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="im-card glass-panel flex-grow"
                >
                    <div className="im-header">
                        <div className="search-bar">
                            <Search className="search-icon" size={20} />
                            <input
                                type="text"
                                placeholder="Search by name, item code, alloy code, or customer..."
                                className="input-field with-icon"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-3 items-center">
                            <button className="btn-secondary" onClick={() => setIsSettingsModalOpen(true)}>
                                <Save size={18} />
                                Elemental Settings
                            </button>
                            <button className="btn-primary" onClick={() => handleOpenModal()}>
                                <Plus size={20} />
                                New Specification
                            </button>
                        </div>
                    </div>

                    <div className="table-container">
                        <table className="items-table">
                            <thead>
                                <tr>
                                    <th>Item Code</th>
                                    <th>Item Name</th>
                                    <th>Alloy Code</th>
                                    <th>Customer</th>
                                    <th>Elements Defined</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((item, index) => (
                                    <React.Fragment key={item.id}>
                                        <motion.tr
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                            className={expandedItemId === item.id ? 'expanded-row-active' : ''}
                                        >
                                            <td>
                                                <span className="code-badge">{item.itemCode}</span>
                                            </td>
                                            <td>
                                                <div className="item-name-cell">
                                                    <Box size={16} className="item-icon" />
                                                    {item.name}
                                                </div>
                                            </td>
                                            <td>{item.alloyCode}</td>
                                            <td>{item.customerName ? item.customerName : '--'}</td>
                                            <td>
                                                <div className="compound-count">
                                                    <Beaker size={14} />
                                                    {item.compounds.length} elements
                                                </div>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className={`icon-btn view ${expandedItemId === item.id ? 'active' : ''}`}
                                                        onClick={() => toggleExpand(item.id)}
                                                        title="View Composition"
                                                    >
                                                        {expandedItemId === item.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                    <button className="icon-btn edit" onClick={() => handleOpenModal(item)} title="Edit">
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button className="icon-btn delete" onClick={() => handleDelete(item.id)} title="Delete">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </motion.tr>

                                        <AnimatePresence>
                                            {expandedItemId === item.id && (
                                                <tr className="expanded-details-row">
                                                    <td colSpan="5" className="p-0">
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                            className="expanded-details-container"
                                                        >
                                                            <div className="expanded-details-inner glass-panel-inner">
                                                                <h4 className="details-title">
                                                                    <Beaker size={16} /> Composition Profile - {item.name}
                                                                </h4>
                                                                {item.compounds.length > 0 ? (
                                                                    <div className="composition-grid">
                                                                        {item.compounds.map((comp, idx) => {
                                                                            const min = comp.target_min != null ? comp.target_min : comp.spec_min;
                                                                            const max = comp.target_max != null ? comp.target_max : comp.spec_max;
                                                                            const rangeText = (isCopperElement(comp.element) && (max == null || max === '' || Number(max) <= 0))
                                                                                ? `≥ ${min}% (No Max)`
                                                                                : `${min}% - ${max}%`;
                                                                            return (
                                                                                <div key={idx} className="comp-badge">
                                                                                    <span className="comp-element">{comp.element}</span>
                                                                                    <span className="comp-range">{rangeText}</span>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-secondary text-sm">No composition defined.</p>
                                                                )}
                                                            </div>
                                                        </motion.div>
                                                    </td>
                                                </tr>
                                            )}
                                        </AnimatePresence>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>

                        {filteredItems.length === 0 && (
                            <div className="empty-state">
                                <Box size={48} color="#45f3ff" className="mb-4" />
                                <p>No specifications found in the database.</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </main>

            {/* Document Preview Modal */}
            <AnimatePresence>
                {specPreviewModal && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{ zIndex: 1100 }}
                    >
                        <motion.div
                            className="spec-preview-modal glass-panel"
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                        >
                            <div className="modal-header-flex">
                                <div className="flex items-center gap-3">
                                    <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(14, 165, 233, 0.1)', color: '#0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <FileText size={22} />
                                    </div>
                                    <div>
                                        <h3 className="text-gradient" style={{ fontSize: '1.25rem', marginBottom: 2 }}>
                                            Specification Preview
                                        </h3>
                                        <div className="flex items-center gap-2 text-xs text-secondary">
                                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{specPreviewModal.fileName}</span>
                                            <span>•</span>
                                            <span>{specPreviewModal.fileSize}</span>
                                            <span>•</span>
                                            <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, background: '#f1f5f9', color: '#475569', textTransform: 'uppercase' }}>
                                                {specPreviewModal.isPdf ? 'PDF Document' : 'Image File'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button className="close-btn" onClick={handleCloseSpecPreview} disabled={specPreviewModal.isExtracting}>
                                    <X size={22} />
                                </button>
                            </div>

                            {/* Preview Body */}
                            <div className="spec-preview-body">
                                {specPreviewModal.isExtracting && (
                                    <div className="spec-extract-overlay">
                                        <div className="spec-extract-spinner-box">
                                            <RefreshCw size={28} className="spin text-primary" />
                                        </div>
                                        <h4 style={{ margin: '0 0 6px 0', fontSize: '17px', fontWeight: 600 }}>
                                            Extracting Specification Data...
                                        </h4>
                                        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center', maxWidth: 420 }}>
                                            AI Vision is analyzing the alloy codes, chemical limits, and composition matrix.
                                        </p>
                                    </div>
                                )}

                                {specPreviewModal.isPdf ? (
                                    <div className="spec-pdf-preview-box">
                                        <iframe
                                            src={specPreviewModal.previewUrl}
                                            title={specPreviewModal.fileName}
                                            className="spec-pdf-frame"
                                        />
                                        <div className="spec-preview-hint">
                                            <span>PDF rendered in native viewer.</span>
                                            <a
                                                href={specPreviewModal.previewUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="spec-external-link"
                                            >
                                                Open in new tab <ExternalLink size={13} />
                                            </a>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="spec-image-preview-box">
                                        <div
                                            className="spec-image-inner"
                                            style={{
                                                transform: `scale(${specPreviewModal.zoom || 1})`,
                                                transition: 'transform 0.15s ease'
                                            }}
                                        >
                                            <img
                                                src={specPreviewModal.previewUrl}
                                                alt="Uploaded specification preview"
                                                className="spec-preview-img"
                                            />
                                        </div>
                                        <div className="spec-zoom-toolbar">
                                            <button
                                                type="button"
                                                className="btn-secondary small"
                                                onClick={() => setSpecPreviewModal(prev => ({ ...prev, zoom: Math.min((prev.zoom || 1) + 0.25, 3) }))}
                                                title="Zoom In"
                                            >+</button>
                                            <button
                                                type="button"
                                                className="btn-secondary small"
                                                onClick={() => setSpecPreviewModal(prev => ({ ...prev, zoom: Math.max((prev.zoom || 1) - 0.25, 0.5) }))}
                                                title="Zoom Out"
                                            >−</button>
                                            <button
                                                type="button"
                                                className="btn-secondary small"
                                                onClick={() => setSpecPreviewModal(prev => ({ ...prev, zoom: 1 }))}
                                                title="Reset Zoom"
                                            >Reset</button>
                                            <span className="spec-zoom-badge">
                                                {Math.round((specPreviewModal.zoom || 1) * 100)}%
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Actions */}
                            <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label className="btn-secondary small cursor-pointer flex items-center gap-1.5" style={{ margin: 0 }}>
                                    <Upload size={14} /> Change File
                                    <input
                                        type="file"
                                        accept="image/*,application/pdf,.pdf"
                                        style={{ display: 'none' }}
                                        onChange={handleSpecFileSelect}
                                        disabled={specPreviewModal.isExtracting}
                                    />
                                </label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        onClick={handleCloseSpecPreview}
                                        disabled={specPreviewModal.isExtracting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primary flex items-center gap-2"
                                        onClick={handleProceedSpecExtract}
                                        disabled={specPreviewModal.isExtracting}
                                    >
                                        {specPreviewModal.isExtracting ? (
                                            <><RefreshCw size={16} className="spin" /> Extracting...</>
                                        ) : (
                                            <><Sparkles size={16} /> Proceed to Setup</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Setup Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="setup-modal glass-panel"
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                        >
                            <div className="modal-header-flex">
                                <h3 className="text-gradient">
                                    {editingItem ? 'Edit Specification' : 'New Specification Setup'}
                                </h3>
                                <button className="close-btn" onClick={handleCloseModal}>
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="setup-form">
                                <div className="form-grid">
                                    <div className="input-group-col">
                                        <label>Item Code</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                name="itemCode"
                                                className="input-field w-full"
                                                placeholder="e.g. STL-HC-001"
                                                value={formData.itemCode}
                                                onChange={handleChange}
                                                required
                                            />
                                            {formData.itemCode && formData.itemCode.trim().length > 0 && (
                                                <button
                                                    type="button"
                                                    className={`btn-secondary flex items-center gap-1 bg-indigo-50 text-indigo-700 border-indigo-200 ${isFetchingAI ? 'opacity-70 cursor-not-allowed' : ''}`}
                                                    style={{ flexShrink: 0, padding: '8px 12px' }}
                                                    onClick={handleFetchDetails}
                                                    disabled={isFetchingAI}
                                                >
                                                    {isFetchingAI ? <><RefreshCw size={14} className="spin" /> Extracting...</> : <><Sparkles size={14} /> AI Auto-Fill</>}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex gap-4 w-full">
                                        <div className="input-group-col flex-1">
                                            <label>Item Name</label>
                                            <input
                                                type="text"
                                                name="name"
                                                className="input-field w-full"
                                                placeholder="e.g. High Carbon Steel"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div className="input-group-col flex-1">
                                            <label>Alloy Code</label>
                                            <input
                                                type="text"
                                                name="alloyCode"
                                                className="input-field w-full"
                                                placeholder="e.g. AISI 1045"
                                                value={formData.alloyCode}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="input-group-col w-full mt-4">
                                        <label>Customer Name</label>
                                        <input
                                            type="text"
                                            name="customerName"
                                            className="input-field w-full"
                                            placeholder="e.g. Acme Corp"
                                            value={formData.customerName}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="compounds-section glass-panel-inner">
                                    <div className="section-header">
                                        <h4><Beaker size={18} /> Composition Matrix</h4>
                                        <div className="flex gap-2 items-center">
                                            <label className="btn-secondary small cursor-pointer flex items-center gap-1 mb-0 bg-indigo-50 text-indigo-700 border-indigo-200" title="Extract Composition from PDF or Image Spec Document">
                                                <Upload size={14} /> Upload Spec (PDF / Image)
                                                <input
                                                    type="file"
                                                    accept="image/*,application/pdf,.pdf"
                                                    style={{ display: 'none' }}
                                                    onChange={handleSpecFileSelect}
                                                />
                                            </label>
                                           <button type="button" className="btn-secondary small flex items-center gap-1" onClick={handleDownloadTemplate} title="Download CSV Template">
                                                <Download size={14} /> Download Template
                                            </button>
                                            <label className="btn-secondary small cursor-pointer flex items-center gap-1 mb-0" title="Upload CSV Data">
                                                <Upload size={14} /> Upload Template
                                                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleUploadCSV} />
                                            </label>
                                            <button type="button" className="btn-secondary small flex items-center gap-1" onClick={handleAddCompound}>
                                                <Plus size={14} /> Add Element
                                            </button>
                                        </div>
                                    </div>

                                    <div className="compounds-table-wrapper">
                                        <table className="compounds-table">
                                            <thead>
                                                <tr>
                                                    <th>Compound Element</th>
                                                    <th>Spec Min (%)</th>
                                                    <th>Spec Max (%)</th>
                                                    <th>Target Min (%)</th>

                                                    <th>Target Max (%)</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <AnimatePresence>
                                                    {formData.compounds.map((comp, index) => (
                                                        <motion.tr
                                                            key={index}
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: 'auto' }}
                                                            exit={{ opacity: 0, height: 0 }}
                                                            style={{
                                                                position: 'relative',
                                                                zIndex: openCompoundIndex === index ? 1000 : (formData.compounds.length - index)
                                                            }}
                                                        >
                                                            <td style={{ position: 'relative', zIndex: openCompoundIndex === index ? 1000 : 'auto' }}>
                                                                <SearchableSelect
                                                                    value={comp.element}
                                                                    options={CHEMICAL_COMPOUNDS}
                                                                    disabledOptions={formData.compounds.map((c, i) => i !== index ? c.element : null).filter(Boolean)}
                                                                    onChange={(val) => handleCompoundChange(index, 'element', val)}
                                                                    isOpen={openCompoundIndex === index}
                                                                    onToggle={(open) => setOpenCompoundIndex(open ? index : null)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className="input-field minimal"
                                                                    placeholder="0.00"
                                                                    value={comp.spec_min}
                                                                    onChange={(e) => handleCompoundChange(index, 'spec_min', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className="input-field minimal"
                                                                    placeholder={isCopperElement(comp.element) ? "Optional (No Max)" : "0.00"}
                                                                    value={comp.spec_max}
                                                                    onChange={(e) => handleCompoundChange(index, 'spec_max', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className="input-field minimal"
                                                                    placeholder="0.00"
                                                                    value={comp.target_min}
                                                                    onChange={(e) => handleCompoundChange(index, 'target_min', e.target.value)}
                                                                />
                                                            </td>

                                                            <td>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className="input-field minimal"
                                                                    placeholder={isCopperElement(comp.element) ? "Optional (No Max)" : "0.00"}
                                                                    value={comp.target_max}
                                                                    onChange={(e) => handleCompoundChange(index, 'target_max', e.target.value)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn delete"
                                                                    onClick={() => handleRemoveCompound(index)}
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            </td>
                                                        </motion.tr>
                                                    ))}
                                                </AnimatePresence>
                                            </tbody>
                                        </table>
                                        {formData.compounds.length === 0 && (
                                            <div className="p-4 text-center text-secondary">
                                                No compounds defined. Add elements to complete the specification.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-actions">
                                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary">
                                        <Save size={18} />
                                        Save Specification
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Global Settings Modal */}
            <AnimatePresence>
                {isSettingsModalOpen && (
                    <motion.div className="modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                        <motion.div className="setup-modal glass-panel" style={{ maxWidth: '800px', maxHeight: '85vh' }}>
                            <div className="modal-header-flex">
                                <h3 className="text-gradient">Elemental Loss Factors</h3>
                                <div className="search-bar" style={{ flex: 1, margin: '0 20px', maxWidth: '300px' }}>
                                    <Search className="search-icon" size={18} />
                                    <input
                                        type="text"
                                        className="input-field with-icon"
                                        placeholder="Search element..."
                                        value={settingsSearchTerm}
                                        onChange={(e) => setSettingsSearchTerm(e.target.value)}
                                    />
                                </div>
                                <button className="close-btn" onClick={() => { setIsSettingsModalOpen(false); setSettingsSearchTerm(''); }}><X size={24} /></button>
                            </div>

                            <div className="compounds-section glass-panel-inner" style={{ marginTop: '0' }}>
                                <div className="compounds-table-wrapper" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    <table className="compounds-table">
                                        <thead>
                                            <tr>
                                                <th>Element Name</th>
                                                <th style={{ textAlign: 'center' }}>Loss Factor (%)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {CHEMICAL_COMPOUNDS.filter(el => el.toLowerCase().includes(settingsSearchTerm.toLowerCase())).map(el => {
                                                const setting = globalSettings.find(s => s.element === el);
                                                return (
                                                    <tr key={el}>
                                                        <td className="font-semibold text-slate-700">{el}</td>
                                                        <td>
                                                            {/* <input 
                                                                type="number" 
                                                                className="input-field minimal mx-auto" 
                                                                style={{ width: '100px' }}
                                                                min="0"
                                                                max="100"
                                                                step="1"
                                                                value={setting?.loss_factor ?? 0}
                                                                onChange={(e) => handleGlobalSettingChange(el, e.target.value)}
                                                            /> */}
                                                            <input
                                                                type="number"
                                                                className="input-field minimal mx-auto"
                                                                style={{ width: '100px' }}
                                                                min="0"
                                                                max="100"
                                                                step="1"
                                                                value={setting?.loss_factor ?? '0'}
                                                                onChange={(e) => handleGlobalSettingChange(el, e.target.value)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button className="btn-secondary" onClick={() => { setIsSettingsModalOpen(false); setSettingsSearchTerm(''); }}>Close</button>
                                <button className="btn-primary" onClick={saveGlobalSettings} disabled={isSavingSettings}>
                                    {isSavingSettings ? <><RefreshCw size={18} className="spin" /> Saving...</> : <><Save size={18} /> Save All Factors</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ItemManagement;
